// Phone photos run to 40+ megapixels; nothing we keep from a crop needs more than this.
export const MAX_IMAGE_SIDE = 2048;

// The crop goes up as base64 in a JSON body, which Django caps at
// DATA_UPLOAD_MAX_MEMORY_SIZE (2.5MB) for the whole request.
export const MAX_DATA_URL_LENGTH = 2 * 1024 * 1024;

const loadImage = src => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

/**
 * Shrinks a picked photo so its longer side is at most `maxSide`, before it reaches the
 * cropper. Only local images (data:/blob:) are touched: drawing a remote one would taint
 * the canvas without CORS, and those are already server-side copies.
 */
export async function downscaleImage(src, maxSide = MAX_IMAGE_SIDE) {
    if (!/^(data|blob):/.test(src)) {
        return src;
    }
    let img;
    try {
        img = await loadImage(src);
    } catch (e) {
        return src;
    }
    const scale = maxSide / Math.max(img.naturalWidth, img.naturalHeight);
    if (!(scale < 1)) {
        return src;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    // the browser applies EXIF orientation when drawing, so the result needs none
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // keep png transparency for logos
    const type = src.startsWith('data:image/png') ? 'image/png' : 'image/jpeg';
    return canvas.toDataURL(type, 0.92);
}

/** JPEG data url of the crop, stepping quality down until it fits the upload limit. */
export function cropToDataUrl(canvas, maxLength = MAX_DATA_URL_LENGTH) {
    let quality = 0.9;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    while (dataUrl.length > maxLength && quality > 0.5) {
        quality -= 0.1;
        dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    return dataUrl;
}
